import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import {
  postRageExit,
  postRedeem,
  getStakingDashboard,
  getXLonBalance,
} from '~/apis'
import { StyledFullPageModalContentOverlay } from '~/components/FullPageModal/styled'
import { PositiveInput } from '~/components/Input'
import Spin from '~/components/Loading/Spin'
import { message } from '~/components/Message'
import TradeDetail from '~/components/TradeDetail'
import { TRADE_STATUS, TRADE_TYPE } from '~/components/TradeDetail/types'
import { useModalToggle, useFeeData } from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import { formatNumber, toBigNumber } from '~/utils/numbers'
import { gasProtect } from '~/utils/validate'
import RedeemLonBlurLayer from './RedeemLonBlurLayer'
import { StyledRedeemLonModal } from '../styled'
import { handleTxError } from '~/utils/handleTxError'
import {
  useGA,
  useLONStakingContract,
  useV6SDK,
  useUpdaterChainId,
} from '~/hooks'
import { calcGasFee } from '~/components/Exchange/helpers'
import { useNativeTokenBalance } from '~/state/wallet/hooks'
import { parseUnits } from 'ethers/lib/utils'
import { gasSpeedup } from '~/utils/gasSpeedup'
import { useWeb3React } from '@web3-react/core'

interface Props {
  onComplete: () => void
  rageExitPreview: {
    penaltyAmount?: string
    receiveAmount?: string
  }
  remainingSeconds: number
}

const RedeemLonDeposit: React.FC<Props> = ({
  onComplete,
  rageExitPreview,
  remainingSeconds,
}) => {
  const { t } = useTranslation()
  const { account } = useWeb3React()
  const chainId = useUpdaterChainId()
  const { signer } = useV6SDK()
  const toggleModalRedemption = useModalToggle(ApplicationModal.LON_REDEMPTION)
  const { trackClick } = useGA()
  const ethBalance = useNativeTokenBalance()
  const feeData = useFeeData()

  const [inputBalance, setInputBalance] = useState('')
  const [showBlurLayer, setBlurLayer] = useState(false)
  const [txHash, setTxHash] = useState('')
  const [status, setStatus] = useState<TRADE_STATUS>()
  const [redeemDetails, setRedeemDetails] = useState<{
    redeemAmount: string
    receiveAmount: string
  }>()

  const inputBalanceBN = toBigNumber(inputBalance)
  const { data: stakingDashboard } = useSWR(
    ['getStakingDashboard', chainId],
    () => getStakingDashboard(chainId),
  )
  const scaleIndex = stakingDashboard?.scaleIndex
  const { data: xLonBalance } = useSWR(
    ['getXLonBalance', chainId, account],
    () => getXLonBalance(chainId, account),
  )

  const penalty = rageExitPreview?.penaltyAmount
  const lonReceive = rageExitPreview?.receiveAmount

  const rageExit = remainingSeconds > 0
  const redeemAmount = rageExit ? xLonBalance : inputBalance
  const receiveAmount = rageExit
    ? lonReceive
    : toBigNumber(scaleIndex).multipliedBy(Number(inputBalance)).toString()
  const request = rageExit ? postRageExit : postRedeem
  const LONStakingContract = useLONStakingContract()

  const estimateGas = async () => {
    try {
      const amount = parseUnits(redeemAmount, 18).toString()
      const gasLimit = rageExit
        ? await LONStakingContract.estimateGas.rageExit()
        : await LONStakingContract.estimateGas.redeem(amount)
      return gasSpeedup(gasLimit)
    } catch (error) {}
  }

  const handleRedeem = async () => {
    const trackKey = rageExit
      ? 'webtl_staking_mandatory_confirm'
      : 'webtl_staking_redeem_confirm'

    if (inputBalanceBN.gt(toBigNumber(xLonBalance))) {
      message.error(t('withdraw_amount_error_tips'))
      return
    }
    if (!rageExit && (!inputBalance || inputBalanceBN.lte(0))) {
      message.error(t('unvalid_input_amount'))
      return
    }
    if (!showBlurLayer) {
      setBlurLayer(true)
      return
    }

    const gasPrice = feeData.gasPrice
    const gasLimit = await estimateGas()
    if (!gasLimit) {
      message.error(t('tokenlon_network_request_failed'))
      return
    }
    const fee = calcGasFee(gasPrice.toString(), gasLimit?.toString())
    const enough = ethBalance.isGreaterThan(fee)
    if (!enough) {
      message.error(t('provider_error_code_INSUFFICIENT_FUNDS'))
      return
    }

    setStatus(TRADE_STATUS.UN_BROADCAST)
    setRedeemDetails({
      redeemAmount,
      receiveAmount,
    })
    try {
      const hash = await request(signer, redeemAmount, gasProtect)
      if (hash) {
        setTxHash(hash)
        setStatus(TRADE_STATUS.PENDING)
      }
      try {
        const receipt = await signer.publicClient.waitForTransactionReceipt({
          hash,
        })
        if (receipt.status) {
          setStatus(TRADE_STATUS.SUCCESS)
          trackClick(trackKey, {
            is_success: 1,
            amount: redeemAmount,
          })
        }
      } catch (e) {
        setStatus(TRADE_STATUS.FAILED)
        const errorMessage =
          typeof e === 'string' ? e : e?.reason || e?.message || e?.code
        if (errorMessage?.toLowerCase()?.includes('user rejected')) {
          message.error(t('user_rejected_message'))
        } else {
          handleTxError(e)
        }
        trackClick(trackKey, {
          is_success: 0,
          amount: redeemAmount,
          message: e?.message,
        })
      }
    } catch (e) {
      setStatus(undefined)
      toggleModalRedemption()
      const errorMessage =
        typeof e === 'string' ? e : e?.reason || e?.message || e?.code
      if (errorMessage?.toLowerCase()?.includes('user rejected')) {
        message.error(t('user_rejected_message'))
      } else {
        handleTxError(e)
      }
      trackClick(trackKey, {
        is_success: 0,
        amount: redeemAmount,
        message: e?.message,
      })
    }
  }

  const handleInputChange = (value: string) => {
    setInputBalance(value)
  }

  const handleFillAll = () => {
    if (Number(xLonBalance) > 0) {
      setInputBalance(xLonBalance)
    }
  }

  if (status && status !== TRADE_STATUS.UN_BROADCAST) {
    return (
      <TradeDetail
        fromToken={`${formatNumber(redeemDetails?.redeemAmount, 6)} xLON`}
        sectionData={[
          {
            label: t('current_rate'),
            value: `xLON: LON ≈ ${formatNumber(scaleIndex, 4)}`,
          },
        ]}
        toToken={`≈${formatNumber(redeemDetails?.receiveAmount, 2)} LON`}
        tradeType={TRADE_TYPE.STAKING}
        txHash={txHash}
        onSuccess={onComplete}
        status={status}
      />
    )
  }

  return (
    <StyledRedeemLonModal>
      <p className="redeem-title">{t('redeem_lon_deposit')}</p>
      <RedeemLonBlurLayer
        status={status}
        showBlurLayer={showBlurLayer}
        onRedeemConfirmClick={handleRedeem}
      >
        {rageExit && (
          <section className="wait-cooldown-tips">
            {t('staking_unstake_tips')}
          </section>
        )}
        <section className="main-content">
          <p className="extractable">
            <span className="title">{t('extractable')}</span>
            <span className="value">{formatNumber(xLonBalance, 4)} xLON</span>
          </p>
          {rageExit ? (
            <p className="pay-fine">
              <span className="title">{t('redeem_penalty_title')}</span>
              <span className="value">
                - {formatNumber(toBigNumber(penalty).dividedBy(scaleIndex), 4)}{' '}
                xLON
              </span>
            </p>
          ) : (
            <section className="widthraw-area">
              <p className="amount-title">{t('extract_amount')}</p>
              <PositiveInput
                className="redeem-input-amount"
                onChange={handleInputChange}
                placeholder={t('input_xlon_amout_placeholder')}
                value={inputBalance}
              />
              <button className="all-btn" onClick={handleFillAll}>
                {t('staking_amount_all')}
              </button>
            </section>
          )}
          <p className="estimate-rate">
            <span className="title">{t('current_rate')}</span>
            <span className="value">
              xLON : LON = {formatNumber(scaleIndex, 6)}
            </span>
          </p>
        </section>
        <section className="estimate-redeem">
          <span className="title">{t('estimate_redeem')}</span>
          <span className="value">{formatNumber(receiveAmount, 4)} LON</span>
        </section>
        <section className="bottom-btn-area">
          <button className="confirm-btn" onClick={handleRedeem}>
            {!!status ? <Spin /> : t('confirm')}
          </button>
        </section>
      </RedeemLonBlurLayer>
      {!!status && <StyledFullPageModalContentOverlay />}
    </StyledRedeemLonModal>
  )
}

export default RedeemLonDeposit
